import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { PageHeader, SignalBadge } from "@/components/Badges";

const PAIRS = ["EURUSD", "GBPUSD", "USDJPY", "USDCHF", "AUDUSD", "NZDUSD", "USDCAD", "EURGBP", "EURJPY", "GBPJPY", "EURAUD", "GBPAUD", "AUDJPY", "CADJPY", "EURCAD"];

export default function AIAnalysis() {
  const [params] = useSearchParams();
  const [pair, setPair] = useState(params.get("pair") || "EURUSD");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const reports = usePoll("/ai/reports", 0);
  const models = usePoll("/ml/models", 0);
  const run = async () => {
    setBusy(true); setText(""); setErr("");
    try {
      const res = await fetch(`${API}/ai/commentary`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ symbol: pair }) });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith("data: ")) return;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setText((t) => t + ev.delta);
          if (ev.error) setErr(ev.error);
        });
      }
      reports.reload();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ai-analysis-page">
      <PageHeader title="AI Analysis" sub="Claude-written commentary grounded in Fx Cotrader's quantitative engine" />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 panel ai-glow p-5 min-h-[420px]">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select value={pair} onChange={(e) => setPair(e.target.value)} data-testid="ai-pair-select"
              className="bg-white border border-zinc-200 rounded-sm text-sm px-2 py-1.5 num focus:outline-none focus:border-[#8B5CF6]">
              {PAIRS.map((p) => <option key={p} value={p}>{p.slice(0, 3)}/{p.slice(3)}</option>)}
            </select>
            <button onClick={run} disabled={busy} data-testid="ai-generate-btn"
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-[#8B5CF6] hover:bg-[#9d74f7] active:scale-95 transition-[background-color,transform] rounded-sm disabled:opacity-60 text-white">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{busy ? "Analyzing…" : "Generate commentary"}
            </button>
          </div>
          {err && <p className="text-sm text-[#DC2626] mb-3" data-testid="ai-error">{err}</p>}
          {text ? <div className="md" data-testid="ai-commentary-output"><ReactMarkdown>{text}</ReactMarkdown></div>
            : !busy && <p className="text-sm text-zinc-500">Select a pair and generate a market read. The AI only interprets Fx Cotrader's computed data — it never invents prices.</p>}
        </div>
        <aside className="col-span-12 lg:col-span-4 space-y-4">
          <div className="panel p-4" data-testid="ml-models-panel">
            <p className="label-xs mb-3">ML Model Registry</p>
            {(models.data?.models || []).map((m) => (
              <div key={m.key} className="flex justify-between py-1.5 border-b border-zinc-100 text-xs">
                <span className={m.active ? "text-zinc-900" : "text-zinc-500"}>{m.name}</span>
                <span className={m.active ? "text-[#8B5CF6]" : m.trained ? "text-zinc-700" : "text-zinc-400"}>{m.active ? "ACTIVE" : m.trained ? "READY" : "NOT TRAINED"}</span>
              </div>
            ))}
            <p className="text-[10px] text-zinc-500 mt-3">Walk-forward splits with an embargo gap prevent look-ahead leakage when training future models.</p>
          </div>
          <div className="panel p-4" data-testid="ai-reports-panel">
            <p className="label-xs mb-3">Recent reports</p>
            {(reports.data || []).map((r, i) => (
              <button key={i} onClick={() => setText(r.content)} className="w-full text-left flex items-center justify-between py-2 border-b border-zinc-100 hover:bg-zinc-50 px-1 transition-colors" data-testid={`ai-report-${i}`}>
                <span className="num text-xs">{r.symbol}</span><SignalBadge signal={r.signal} /><span className="num text-[10px] text-zinc-500">{new Date(r.created_at).toUTCString().slice(17, 22)}</span>
              </button>
            ))}
            {!reports.data?.length && <p className="text-xs text-zinc-500">No reports yet.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
